/**
 * SE-6490 — the pipeline player card, rebuilt against Figma `Player Card v7`.
 *
 * @remarks
 * Row order and treatment come from the design node; the ticket is
 * authoritative for what each row binds to:
 *
 * 1. identity — avatar · name · GPR badge      ┐ `PlayerCardHeader`
 * 2. context  — position chip · club · age · contract ┘
 * 3. signals  — verdict pill · reports pill    → `PlayerCardSignals`
 * 4. tags     — removable chips · `+N`         → `PlayerCardTags`
 * 5. footer   — muted "Updated …"              → `PlayerCardFooter`
 *
 * The card's own chrome is the ratified token mapping for this component
 * (`MobileTargetCardView`, per the design registry): `rounded-md border
 * border-outline-subtle bg-surface-base p-3` with an 8px row gap. That
 * replaced a runtime style object whose always-on 2px blue border was the
 * "stuck focus ring" this ticket reports — see `PlayerCardBaseContainer`.
 * @module components/organisms/custom/ui/PlayerCardBase/PlayerCardBaseView
 */
import React, { memo } from "react";

import PlayerCardGestureHandler from "../PlayerCardGestureHandler/PlayerCardGestureHandlerContainer";
import PlayerCardContent from "@/components/organisms/custom/ui/PlayerCardContent";

import { Box } from "@/components/atoms/Box";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/box, @/components/ui/pressable) —…
import { Pressable as UIPressable } from "@/components/ui/pressable";
import type { KanbanColors } from "@/features/player-kanban/constants/colors";

/**
 *
 */
interface PlayerCardBaseViewProps {
  playerId: string;
  playerName: string;
  rank?: number;
  daysInStage?: number | null;
  isStale?: boolean;
  /** SE-5492: canonical player image URL; null for every player today (SE-5538). */
  imageUrl?: string | null;
  /** SE-5492: explicit position, distinct from the role archetype. */
  position?: string;
  /** SE-5492: total scout reports attached to the player. */
  reportsCount?: number;
  /** SE-5492: latest report's overall score, or null when unscored. */
  verdict?: string | null;
  archetype: string;
  age: number;
  gpr: number;
  contractValue: string;
  contractMonths: number | null;
  agentName: string;
  teamName: string;
  playerAgent?: string;
  /** SE-6490: the backing card's `updatedAt`, driving the footer. */
  updatedAt?: string | null;
  /** SE-6866: the resolved last-editor name completing the footer; null hides it. */
  editorName?: string | null;
  displayedTags: { name: string; color: string }[];
  _expandedTagIndex: number | null;
  _showAllTags: boolean;
  _hasMoreTags: boolean;
  _isHovered?: boolean;
  showMoreTags?: boolean;
  isKanban?: boolean;
  getPressableStyle: ({ pressed }: { pressed: boolean }) => object[];
  useCustomGestures: boolean;
  colors: KanbanColors;
  onPress: () => void;
  onAddTag?: () => void;
  onRemoveTag?: (tagName: string) => void;
  _onToggleTagExpansion: (index: number) => void;
  _onToggleShowAllTags: () => void;
  onToggleShowMore?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  onPreventDrag?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  showRemoveButtons?: boolean;
  bioData?: {
    customStats?: Record<string, unknown> | null;
    minGeminiPlayerValuation?: number | null;
    maxGeminiPlayerValuation?: number | null;
    [key: string]: unknown;
  };
}

/**
 * The card face.
 *
 * A helper rather than a local const: both gesture branches render the same
 * body — the fork is only about which press wrapper surrounds it — and a View
 * may not hold statements (SE-8019).
 * @param props - View props.
 * @returns The card body.
 */
const renderCardContent = (
  props: PlayerCardBaseViewProps
): React.ReactElement => (
  <PlayerCardContent
    playerName={props.playerName}
    isStale={props.isStale}
    imageUrl={props.imageUrl}
    position={props.position}
    reportsCount={props.reportsCount}
    verdict={props.verdict}
    teamName={props.teamName}
    age={props.age}
    contractMonths={props.contractMonths}
    gpr={props.gpr}
    updatedAt={props.updatedAt}
    editorName={props.editorName}
    displayedTags={props.displayedTags}
    isHovered={props._isHovered ?? false}
    playerId={props.playerId}
    onRemoveTag={props.onRemoveTag}
    onPreventDrag={props.onPreventDrag}
    showRemoveButtons={props.showRemoveButtons ?? false}
  />
);

/**
 * View component for base player card with conditional gesture handling
 * @param props - Component props
 */
const PlayerCardBaseView = (props: PlayerCardBaseViewProps) => (
  <>
    {props.useCustomGestures ? (
      <PlayerCardGestureHandler
        playerId={props.playerId}
        playerName={props.playerName}
        onPress={props.onPress}
        onDragStart={props.onDragStart}
        onDragEnd={props.onDragEnd}
      >
        <Box className="pb-1">
          <UIPressable
            onPress={undefined}
            onHoverIn={props.onHoverIn}
            onHoverOut={props.onHoverOut}
            // eslint-disable-next-line no-restricted-syntax -- Pressable style-as-function (pressed-state transform) not expressible via atom UNSAFE_style
            style={props.getPressableStyle}
          >
            {renderCardContent(props)}
          </UIPressable>
        </Box>
      </PlayerCardGestureHandler>
    ) : (
      <Box className="pb-1">
        <UIPressable
          onPress={props.onPress}
          onHoverIn={props.onHoverIn}
          onHoverOut={props.onHoverOut}
          // eslint-disable-next-line no-restricted-syntax -- Pressable style-as-function (pressed-state transform) not expressible via atom UNSAFE_style
          style={props.getPressableStyle}
        >
          {renderCardContent(props)}
        </UIPressable>
      </Box>
    )}
  </>
);

PlayerCardBaseView.displayName = "PlayerCardBaseView";

export default memo(PlayerCardBaseView);
